// Meta Pixel, loaded only after marketing consent. The loader is the standard
// Events Manager snippet without its PageView: the app sends only
// CompleteRegistration and Purchase. The server sends the same events through
// the Conversions API with the same event IDs, and Meta counts each pair once.

import type { ProductTier } from '../../types/shared';
import { TIER_NAMES } from '../productTier';

interface Fbq {
  (...args: unknown[]): void;
  callMethod?: (...args: unknown[]) => void;
  queue: unknown[];
  push: Fbq;
  loaded: boolean;
  version: string;
  disablePushState?: boolean;
}

declare global {
  interface Window {
    fbq?: Fbq;
    _fbq?: Fbq;
  }
}

const SCRIPT_SRC = 'https://connect.facebook.net/en_US/fbevents.js';
const PIXEL_COOKIES = ['_fbp', '_fbc'];

let initialized = false;
let active = false;

const installLoader = (): Fbq => {
  if (window.fbq) return window.fbq;
  const fbq = ((...args: unknown[]) => {
    if (fbq.callMethod) {
      fbq.callMethod(...args);
    } else {
      fbq.queue.push(args);
    }
  }) as Fbq;
  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = '2.0';
  fbq.queue = [];
  window.fbq = fbq;
  if (!window._fbq) window._fbq = fbq;

  const script = document.createElement('script');
  script.async = true;
  script.src = SCRIPT_SRC;
  document.head.appendChild(script);
  return fbq;
};

/** Loads and initializes the Pixel once; after a withdrawal it grants again. */
export const loadPixel = (pixelId: string): void => {
  if (active) return;
  if (initialized) {
    window.fbq?.('consent', 'grant');
    active = true;
    return;
  }
  const fbq = installLoader();
  // Otherwise the Pixel sends a PageView on every route change by itself.
  fbq.disablePushState = true;
  fbq('init', pixelId);
  initialized = true;
  active = true;
};

export const revokeConsent = (): void => {
  if (!active) return;
  window.fbq?.('consent', 'revoke');
  active = false;
};

export const isPixelActive = (): boolean => active;

/** Sends an event with the ID the server uses for the same occurrence; no-op without consent. */
export const trackEvent = (
  name: string,
  params: Record<string, unknown>,
  eventId: string
): void => {
  if (!active || !window.fbq) return;
  window.fbq('track', name, params, { eventID: eventId });
};

// Event IDs match the server's (backend marketing.constants.EVENT_ID_PREFIXES).
export const trackCompleteRegistration = (organizationId: string, tier: ProductTier): void =>
  trackEvent('CompleteRegistration', { content_name: TIER_NAMES[tier] }, `cr-${organizationId}`);

/** The first booking of any tier; value is the net amount in EUR. */
export const trackPurchase = (
  organizationId: string,
  tier: ProductTier,
  value: number | undefined
): void =>
  trackEvent(
    'Purchase',
    {
      ...(value !== undefined && Number.isFinite(value) && value >= 0 ? { value } : {}),
      currency: 'EUR',
      content_name: TIER_NAMES[tier],
    },
    `purchase-${organizationId}`
  );

/**
 * Deletes the Pixel cookies after a withdrawal. The Pixel sets them on the
 * top domain (.novofon.ai), so every parent domain of the host is tried.
 */
export const deletePixelCookies = (hostname = window.location.hostname): void => {
  const domains: Array<string | null> = [null];
  if (!/^[\d.]+$/.test(hostname)) {
    const labels = hostname.split('.');
    for (let i = 0; i <= labels.length - 2; i += 1) {
      domains.push(`.${labels.slice(i).join('.')}`);
    }
  }
  for (const name of PIXEL_COOKIES) {
    for (const domain of domains) {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${
        domain ? `; domain=${domain}` : ''
      }`;
    }
  }
};
